import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  HardDrive,
  Table2,
  BookOpen,
  ArrowRight,
  Plus,
} from 'lucide-react';
import type { StorageStats } from '@/types/storage-stats';

interface StorageCardProps {
  data: StorageStats;
}

export function StorageCard({ data }: StorageCardProps) {
  const { dataTables, knowledgeBase, combined } = data;

  // Top 5 items by size
  const topTables = dataTables.tables.slice(0, 5);
  const topCollections = knowledgeBase.collections.slice(0, 5);

  const maxTableSize = Math.max(...topTables.map((t) => t.storageMB), 0.01);
  const maxCollectionSize = Math.max(...topCollections.map((c) => c.storageMB), 0.01);

  const formatNumber = (n: number) => n.toLocaleString();
  const formatMB = (mb: number) => {
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    if (mb >= 0.001) return `${(mb * 1024).toFixed(0)} KB`;
    return '0 KB';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HardDrive className="h-5 w-5" />
          Data Storage
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Summary Boxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Data Tables Summary */}
          <div className="border rounded-lg p-4 bg-muted/30 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <Table2 className="h-5 w-5 text-purple-500" />
              <span className="font-medium">Data Tables</span>
            </div>
            {dataTables.totalTables > 0 ? (
              <>
                <div className="space-y-1 text-sm mb-3 flex-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tables</span>
                    <span className="font-medium">{dataTables.totalTables}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rows</span>
                    <span className="font-medium">{formatNumber(dataTables.totalRows)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Storage</span>
                    <span className="font-medium">{formatMB(dataTables.totalStorageMB)}</span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="w-full mt-auto" asChild>
                  <Link to="/data-tables">
                    View Tables <ArrowRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              </>
            ) : (
              <div className="text-center py-2">
                <p className="text-sm text-muted-foreground mb-2">No tables yet</p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/data-tables">
                    <Plus className="h-4 w-4 mr-1" /> Create Table
                  </Link>
                </Button>
              </div>
            )}
          </div>

          {/* Knowledge Base Summary */}
          <div className="border rounded-lg p-4 bg-muted/30 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="h-5 w-5 text-blue-500" />
              <span className="font-medium">Knowledge Base</span>
            </div>
            {knowledgeBase.totalCollections > 0 ? (
              <>
                <div className="space-y-1 text-sm mb-3 flex-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Collections</span>
                    <span className="font-medium">{knowledgeBase.totalCollections}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Documents</span>
                    <span className="font-medium">{knowledgeBase.totalDocuments}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Chunks</span>
                    <span className="font-medium">{formatNumber(knowledgeBase.totalChunks)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Storage</span>
                    <span className="font-medium">{formatMB(knowledgeBase.totalStorageMB)}</span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="w-full mt-auto" asChild>
                  <Link to="/knowledge-base">
                    View Collections <ArrowRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              </>
            ) : (
              <div className="text-center py-2">
                <p className="text-sm text-muted-foreground mb-2">No collections yet</p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/knowledge-base">
                    <Plus className="h-4 w-4 mr-1" /> Create Collection
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Top Items by Size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Top Data Tables */}
          <div>
            <h4 className="text-sm font-medium mb-3">Top Data Tables by Size</h4>
            {topTables.length > 0 ? (
              <div className="space-y-2">
                {topTables.map((table) => (
                  <div key={table.id} className="flex items-center gap-2">
                    <div className="w-24 truncate text-sm" title={table.name}>
                      {table.name}
                    </div>
                    <div className="flex-1">
                      <Progress
                        value={(table.storageMB / maxTableSize) * 100}
                        accent="purple"
                        variant="mini"
                      />
                    </div>
                    <div className="w-16 text-right text-sm text-muted-foreground">
                      {formatMB(table.storageMB)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No tables</p>
            )}
          </div>

          {/* Top KB Collections */}
          <div>
            <h4 className="text-sm font-medium mb-3">Top KB Collections by Size</h4>
            {topCollections.length > 0 ? (
              <div className="space-y-2">
                {topCollections.map((collection) => (
                  <div key={collection.id} className="flex items-center gap-2">
                    <div className="w-24 truncate text-sm" title={collection.name}>
                      {collection.name}
                    </div>
                    <div className="flex-1">
                      <Progress
                        value={(collection.storageMB / maxCollectionSize) * 100}
                        accent="info"
                        variant="mini"
                      />
                    </div>
                    <div className="w-16 text-right text-sm text-muted-foreground">
                      {formatMB(collection.storageMB)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No collections</p>
            )}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Total */}
        <div className="text-center text-sm text-muted-foreground">
          Total Storage:{' '}
          <span className="font-medium text-foreground">
            {formatMB(combined.totalStorageMB)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
